import { useContext } from 'react'
import { Box, Button, Card, FormControlLabel, Radio, RadioGroup, Typography, useTheme } from '@mui/material'
import { Field, FieldProps, Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'

import { AuthenticationContext } from '../../providers/auth-provider'
import API from '../../utils/api'
import { normalizeDateFormatForSettings, normalizeTimeFormatPreference } from '../../utils/preference-format'
import timezoneList from '../../utils/timezone-list'
import FormikDropdown from '../formik-dropdown'
import FullScreenModal from '../full-screen-modal'

import useStyles from './index.styles'

interface Props {
  open: boolean
  onClose: () => void
}

const timezoneItems = timezoneList.map((tz) => ({ id: tz.tzCode, name: tz.header }))

const SettingsModal = ({ open, onClose }: Props) => {
  const classes = useStyles
  const theme = useTheme()
  const { ownUser, setOwnUser } = useContext(AuthenticationContext)
  const { enqueueSnackbar } = useSnackbar()

  const initialValues = {
    timezone: ownUser?.information?.timezone || '',
    tempUnits: ownUser?.preferences?.tempUnits || 'degF',
    dateFormat: normalizeDateFormatForSettings(ownUser?.preferences?.dateFormat),
    timeFormat: normalizeTimeFormatPreference(ownUser?.preferences?.timeFormat),
  }

  const handleSubmit = async (values: typeof initialValues) => {
    if (!ownUser) return

    const updatedUser = {
      ...ownUser,
      information: {
        ...ownUser.information,
        timezone: values.timezone,
      },
      preferences: {
        ...ownUser.preferences,
        tempUnits: values.tempUnits,
        dateFormat: values.dateFormat,
        timeFormat: values.timeFormat,
      },
    }

    const response = await API.put(`/api/users/${ownUser.id}`, updatedUser)

    if (response.ok) {
      setOwnUser((prev) => {
        if (!prev) return response.body
        return {
          ...prev,
          ...response.body,
          information: {
            ...prev.information,
            ...response.body?.information,
            timezone: values.timezone,
          },
          preferences: {
            ...prev.preferences,
            ...response.body?.preferences,
            tempUnits: values.tempUnits,
            dateFormat: values.dateFormat,
            timeFormat: values.timeFormat,
          },
        }
      })
      enqueueSnackbar('Settings updated successfully', { variant: 'success' })
      onClose()
    } else {
      enqueueSnackbar('Failed to update settings', { variant: 'error' })
    }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
      {({ isSubmitting, submitForm }) => (
        <FullScreenModal
          open={open}
          onClose={onClose}
          showCloseIcon={true}
          title="Settings"
          maxWidth="sm"
          confirmButton={
            <Button type="submit" variant="contained" color="primary" onClick={submitForm} disableElevation disabled={isSubmitting}>
              Save
            </Button>
          }
        >
          <Form>
            <Box sx={classes.regionalSection}>
              <Typography sx={classes.preferencesSubheader}>Regional</Typography>

              <FormikDropdown
                name="timezone"
                label="Time Zones"
                required={false}
                items={timezoneItems}
                disabled={isSubmitting}
                size="medium"
                sx={classes.dropdown(theme)}
              />
            </Box>

            <Box sx={classes.cardSection}>
              <Typography sx={classes.preferencesSubheader}>Temperature</Typography>

              <Card sx={classes.preferencesCard}>
                <Field name="tempUnits">
                  {({ field }: FieldProps) => (
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="degF"
                        control={<Radio />}
                        label="Fahrenheit"
                        labelPlacement="start"
                        disabled={isSubmitting}
                        sx={classes.radioOption}
                      />

                      <FormControlLabel
                        value="degC"
                        control={<Radio />}
                        label="Celsius"
                        labelPlacement="start"
                        disabled={isSubmitting}
                        sx={classes.radioOption}
                      />
                    </RadioGroup>
                  )}
                </Field>
              </Card>
            </Box>

            <Box sx={classes.cardSection}>
              <Typography sx={classes.preferencesSubheader}>Date</Typography>

              <Card sx={classes.preferencesCard}>
                <Field name="dateFormat">
                  {({ field }: FieldProps) => (
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="MM-DD-YYYY"
                        control={<Radio />}
                        label="MM-DD-YYYY"
                        labelPlacement="start"
                        disabled={isSubmitting}
                        sx={classes.radioOption}
                      />

                      <FormControlLabel
                        value="DD.MM.YYYY"
                        control={<Radio />}
                        label="DD-MM-YYYY"
                        labelPlacement="start"
                        disabled={isSubmitting}
                        sx={classes.radioOption}
                      />
                    </RadioGroup>
                  )}
                </Field>
              </Card>
            </Box>

            <Box sx={classes.cardSection}>
              <Typography sx={classes.preferencesSubheader}>Time</Typography>

              <Card sx={classes.preferencesCard}>
                <Field name="timeFormat">
                  {({ field }: FieldProps) => (
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="12hr"
                        control={<Radio />}
                        label="12 HR"
                        labelPlacement="start"
                        disabled={isSubmitting}
                        sx={classes.radioOption}
                      />

                      <FormControlLabel
                        value="24hr"
                        control={<Radio />}
                        label="24 HR"
                        labelPlacement="start"
                        disabled={isSubmitting}
                        sx={classes.radioOption}
                      />
                    </RadioGroup>
                  )}
                </Field>
              </Card>
            </Box>
          </Form>
        </FullScreenModal>
      )}
    </Formik>
  )
}

export default SettingsModal
